import { FaChevronDown } from 'react-icons/fa'
import { FiSearch } from 'react-icons/fi'
import { CountryDropdownProps } from './CountryDropdown.types'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useRef, useCallback } from 'react'

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  items,
  selectedCountry,
  setSelectedCountry,
  showDropdown,
  setShowDropdown,
  onSearchQueryChange,
}) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Compute selected item label for display
  const selectedLabel =
    items.find((item) => item.value === selectedCountry)?.label || ''

  // Filter dropdown items based on current search query
  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  // Notify parent component when search query changes (for external filtering)
  useEffect(() => {
    onSearchQueryChange?.(searchQuery)
  }, [searchQuery, onSearchQueryChange])

  // Reset state when user selects an item (but preserve search when clicking outside)
  const resetSearchState = useCallback(() => {
    setSearchQuery('')
    setHasSearched(false)
  }, [])

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
        // Preserve search query so filtered results remain
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown, setShowDropdown])

  // Handle item selection from dropdown
  const handleSelectItem = (value: string) => {
    setSelectedCountry(value)
    resetSearchState()
    setShowDropdown(false)
  }

  // Handle search input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchQuery(newValue)

    // Mark that user has entered search mode
    if (newValue) {
      setHasSearched(true)
      // Clear selection when user types to enable search mode
      if (selectedCountry) {
        setSelectedCountry('')
      }
    }

    // Auto-open dropdown when typing
    if (!showDropdown) {
      setShowDropdown(true)
    }
  }

  // Open dropdown when input is focused
  // Clear selection and show empty input when user clicks on search
  const handleInputFocus = () => {
    setShowDropdown(true)
    // Clear selection when focusing to show empty input for new search
    if (selectedCountry && !searchQuery) {
      setSelectedCountry('')
      setHasSearched(true) // Mark as searched to prevent reverting to label
    }
  }

  // Toggle dropdown visibility
  const handleChevronClick = () => {
    setShowDropdown(!showDropdown)
  }

  const displayValue = searchQuery || (hasSearched ? '' : selectedLabel)

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--search-icon-color)] pointer-events-none z-10" />
        <input
          ref={searchInputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={t('resultPage.searchPlaceholder', 'Search...')}
          className="result-filter-btn w-full pl-9 pr-12 h-10 min-w-[250px] rounded-md text-base transition border cursor-text focus:outline-none focus:border-[var(--search-focus-border)] placeholder:text-[var(--search-placeholder)]"
        />
        <button
          type="button"
          onClick={handleChevronClick}
          aria-label={t('resultPage.toggleDropdown', 'Toggle dropdown')}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer hover:opacity-70 transition-opacity"
        >
          <FaChevronDown
            className={`transition-transform duration-200 text-[var(--search-icon-color)] ${showDropdown ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Dropdown List */}
      {showDropdown && (
        <ul className="result-filter-dropdown legacy-scrollbar absolute top-full left-0 w-full mt-2 rounded-md overflow-hidden z-30 border max-h-[300px] overflow-y-auto">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, idx) => (
              <li
                key={idx}
                className="result-filter-dropdown-item px-4 py-2 border-b cursor-pointer transition-colors last:border-b-0"
                onClick={() => handleSelectItem(item.value)}
              >
                {item.label}
              </li>
            ))
          ) : (
            <li className="result-filter-dropdown-item px-4 py-3 text-center text-[var(--search-placeholder)] text-sm">
              {t('resultPage.noResults', 'No results found')}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default CountryDropdown
